import { useStaffPermissionAccess } from '@/hooks/use-staff-permission-access';
import { useState, useMemo, useEffect } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { Helmet } from 'react-helmet-async';
import { useOrg } from '@/providers';
import { SettingsPageLayout } from '@/components/layout/settings-page-layout';
import { PageCanvas } from '@/components/layout/page-canvas';
import { useGetStaff } from '@/api/generated/staff/staff';
import type { GetStaff200DataItemsItem } from '@/api/generated/schemas';
import { StaffSidebar } from './staff/layout/staff-sidebar';
import { StaffDetailView } from './staff/layout/staff-detail-view';
import { NewStaffMemberForm } from './staff/forms/new-staff-member-form';
import { SetUpLoginCard } from './staff/modals/set-up-login-card';
import { SetUpLoginFlow } from './staff/modals/set-up-login-flow';
import type { StaffDetail, StaffDetailTab, ContentView, CreatedStaffInfo } from './staff/types';
import { toStaffDetail } from './staff/utils/to-staff-detail';
import { withCreatedLogin, type CreatedStaffLogin } from './staff/utils/staff-login-patches';
import { useSalonHayvenTeam } from './staff/hayven-team/use-salon-hayven-team';
import { useStaffUrlSelection } from './staff/hooks/use-staff-url-selection';
import {
  HayvenTeamMemberDetail,
  HayvenTeamMemberDetailSkeleton,
} from './staff/hayven-team/hayven-team-member-detail';
import { formatDocumentTitle } from '@/lib/document-title';
import { STAFF_LIST_PARAMS } from './staff/constants';

/** The open staff member lives in the URL (`?staff=<id>`, see `useStaffUrlSelection`). */
interface StaffSearch {
  staff?: string;
}

export const Route = createFileRoute('/_auth/$orgId/settings/staff')({
  validateSearch: (search: Record<string, unknown>): StaffSearch => ({
    staff: typeof search.staff === 'string' ? search.staff : undefined,
  }),
  component: StaffSettingsPage,
});

function StaffSettingsPage() {
  useOrg(); // Ensure we're in org context

  // View state for content area
  const [contentView, setContentView] = useState<ContentView>('default');

  // Created staff info for the set-up-login card
  const [createdStaffInfo, setCreatedStaffInfo] = useState<CreatedStaffInfo | null>(null);

  // Newly created staff member whose Set up login dialog is open (from the card)
  const [loginSetupStaff, setLoginSetupStaff] = useState<StaffDetail | null>(null);

  // Selected staff for detail view
  const [selectedStaff, setSelectedStaff] = useState<StaffDetail | null>(null);

  // Selected Hayven agency person (read-only detail view)
  const [selectedHayvenMemberId, setSelectedHayvenMemberId] = useState<string | null>(null);
  const hayvenTeam = useSalonHayvenTeam();
  const selectedHayvenMember =
    hayvenTeam.members.find((member) => member.id === selectedHayvenMemberId) ?? null;

  // The selected Hayven person left the list (access revoked, refetch): drop the
  // stale selection instead of keeping a hidden id around.
  const isHayvenSelectionStale =
    selectedHayvenMemberId !== null && !hayvenTeam.isFetching && selectedHayvenMember === null;
  useEffect(() => {
    if (!isHayvenSelectionStale) return;
    setSelectedHayvenMemberId(null);
    setContentView((view) => (view === 'hayven-member' ? 'default' : view));
  }, [isHayvenSelectionStale]);

  // Active tab in staff detail view
  const [activeTab, setActiveTab] = useState<StaffDetailTab>('details');

  // Show archived staff toggle
  const [showArchived, setShowArchived] = useState(false);

  // Fetch staff list from backend (all staff - we filter client-side for archived toggle).
  const { data: staffResponse, isLoading } = useGetStaff(STAFF_LIST_PARAMS);
  const allStaffList = useMemo(
    () => staffResponse?.data?.items ?? [],
    [staffResponse?.data?.items]
  );

  // Split staff into active and archived lists
  const { activeStaffList, archivedStaffList } = useMemo(() => {
    const active = allStaffList.filter((s) => s.isActive !== false);
    const archived = allStaffList.filter((s) => s.isActive === false);
    return { activeStaffList: active, archivedStaffList: archived };
  }, [allStaffList]);

  // `?staff=` ⇄ the open staff member (links, browser Back/Forward, phone Back to the list)
  const staffSelection = useStaffUrlSelection({
    allStaffList,
    openStaffId: contentView === 'staff-detail' ? (selectedStaff?.id ?? null) : null,
    openStaff,
    closeStaff: () => {
      if (contentView !== 'staff-detail') return;
      setSelectedStaff(null);
      setContentView('default');
    },
    onLinkedTab: setActiveTab,
  });

  const { isAdmin: canManageStaff } = useStaffPermissionAccess();

  // Handle Add Staff Member button click
  const handleAddStaffClick = () => {
    if (!canManageStaff) return;
    staffSelection.clear();
    setSelectedStaff(null);
    setSelectedHayvenMemberId(null);
    setContentView('new-staff');
  };

  // Handle cancel form
  const handleCancelForm = () => {
    setContentView('default');
  };

  // Open the newly created staff member's profile (the API's record: the list may not have refetched yet)
  const showCreatedStaff = (staffDetail: StaffDetail) => {
    setSelectedStaff(staffDetail);
    setSelectedHayvenMemberId(null);
    setActiveTab('details');
    setContentView('staff-detail');
    staffSelection.select(staffDetail.id);
  };

  // Handle successful staff creation - offer to set up their login now (phone login works without an email)
  const handleStaffCreated = (staffInfo: CreatedStaffInfo) => {
    setCreatedStaffInfo(staffInfo);
    setContentView('set-up-login');
  };

  // Handle dismiss of the set-up-login card - auto-select newly created staff
  const handleDismissSetUpLogin = () => {
    if (createdStaffInfo) showCreatedStaff(createdStaffInfo);
    setCreatedStaffInfo(null);
  };

  // Login made: open the profile behind the "can sign in now" dialog (it stays open until Done, with any password)
  const handleCreatedStaffLogin = (created: CreatedStaffLogin) => {
    if (loginSetupStaff) showCreatedStaff(withCreatedLogin(loginSetupStaff, created));
    setCreatedStaffInfo(null);
  };

  // Handle staff update from detail view
  // A slow request can answer after the admin moved on: it only ever updates the profile it was about.
  const handleStaffUpdated = (updatedStaff: StaffDetail) => {
    setSelectedStaff((current) => (current?.id === updatedStaff.id ? updatedStaff : current));
  };

  // Handle staff archived - reset to default view
  const handleStaffArchived = () => {
    staffSelection.clear();
    setSelectedStaff(null);
    setContentView('default');
    setShowArchived(true); // Show archived section so user can see the archived staff
  };

  // Handle staff reactivated - reset to default view
  const handleStaffReactivated = () => {
    staffSelection.clear();
    setSelectedStaff(null);
    setContentView('default');
  };

  // Show a staff member's profile (local state only; callers sync the URL)
  function openStaff(staff: GetStaff200DataItemsItem) {
    setSelectedStaff(toStaffDetail(staff));
    setSelectedHayvenMemberId(null);
    // Keep current tab when switching staff (don't reset to 'details')
    setContentView('staff-detail');
  }

  // Handle staff selection from sidebar
  const handleStaffSelect = (staff: GetStaff200DataItemsItem) => {
    openStaff(staff);
    staffSelection.select(staff.id);
  };

  // Handle Hayven team selection from sidebar
  const handleHayvenMemberSelect = (memberId: string) => {
    staffSelection.clear();
    setSelectedStaff(null);
    setSelectedHayvenMemberId(memberId);
    setContentView('hayven-member');
  };

  // Phone/tablet Back to the list: an open staff profile goes through the URL, the other views are local.
  const handleMobileBack = () => {
    if (contentView === 'staff-detail') {
      staffSelection.back();
      return;
    }
    setCreatedStaffInfo(null);
    setSelectedHayvenMemberId(null);
    setContentView('default');
  };

  const sidebarContent = (
    <StaffSidebar
      activeStaffList={activeStaffList}
      archivedStaffList={archivedStaffList}
      isLoading={isLoading}
      selectedStaffId={selectedStaff?.id}
      showArchived={showArchived}
      canManageStaff={canManageStaff}
      onAddStaffClick={handleAddStaffClick}
      onArchivedToggle={() => setShowArchived((current) => !current)}
      onStaffSelect={handleStaffSelect}
      hayvenTeam={
        hayvenTeam.isVisible
          ? {
              members: hayvenTeam.members,
              isLoading: hayvenTeam.isLoading,
              isError: hayvenTeam.isError,
              isRetrying: hayvenTeam.isFetching,
              onRetry: () => void hayvenTeam.refetch(),
              selectedId: selectedHayvenMemberId,
              viewerAgencyMemberId: hayvenTeam.viewerAgencyMemberId,
              onSelect: (member) => handleHayvenMemberSelect(member.id),
            }
          : undefined
      }
    />
  );

  return (
    <>
      <Helmet defer={false}>
        <title>{formatDocumentTitle('Staff')}</title>
      </Helmet>

      <PageCanvas className="h-full min-h-0 overflow-hidden">
        <SettingsPageLayout
          sidebar={sidebarContent}
          className="h-full min-h-0 overflow-hidden rounded-[var(--r-2xl)] border border-clovie-border-soft bg-clovie-surface shadow-soft-lg"
          sidebarClassName="calendar-scrollbar border-clovie-border-soft bg-clovie-surface text-clovie-text lg:w-[320px]"
          mainClassName="calendar-scrollbar min-w-0 bg-clovie-surface-2 px-4 py-5 sm:px-6 sm:py-5 lg:px-8 lg:py-6"
          mobileDetailOpen={contentView !== 'default'}
          onMobileBack={handleMobileBack}
          mobileBackLabel="Staff"
        >
          {contentView === 'new-staff' && canManageStaff ? (
            <NewStaffMemberForm onCancel={handleCancelForm} onSuccess={handleStaffCreated} />
          ) : contentView === 'set-up-login' && canManageStaff && createdStaffInfo ? (
            <SetUpLoginCard
              staffName={createdStaffInfo.firstName}
              onDismiss={handleDismissSetUpLogin}
              onSetUpLogin={() => setLoginSetupStaff(createdStaffInfo)}
            />
          ) : contentView === 'staff-detail' && selectedStaff ? (
            <StaffDetailView
              staff={selectedStaff}
              activeTab={activeTab}
              onTabChange={setActiveTab}
              onStaffUpdated={handleStaffUpdated}
              onStaffArchived={handleStaffArchived}
              onStaffReactivated={handleStaffReactivated}
            />
          ) : contentView === 'hayven-member' && selectedHayvenMember ? (
            <HayvenTeamMemberDetail member={selectedHayvenMember} />
          ) : contentView === 'hayven-member' && hayvenTeam.isFetching ? (
            // Selected person missing while the list refetches; the stale-selection
            // effect falls back to the default view once it settles.
            <HayvenTeamMemberDetailSkeleton />
          ) : (
            <div className="max-w-xl rounded-[var(--r-2xl)] border border-clovie-border-soft bg-clovie-surface p-6 text-clovie-text shadow-soft">
              <h1 className="text-2xl font-semibold text-clovie-text">Manage your staff</h1>
              <p className="mt-3 text-base leading-6 text-clovie-muted">
                You can create new staff members or change the details, services and work hours of
                existing staff members.
              </p>
            </div>
          )}
        </SettingsPageLayout>
      </PageCanvas>

      {canManageStaff && loginSetupStaff && (
        <SetUpLoginFlow
          staff={loginSetupStaff}
          onClose={() => setLoginSetupStaff(null)}
          onLoginCreated={handleCreatedStaffLogin}
        />
      )}
    </>
  );
}
